/**
 * useAppHandlers - Extracted handler logic from App.tsx
 *
 * This hook consolidates all event handler callbacks that were previously
 * defined inline in App.tsx, reducing the component's complexity.
 * The hook creates and manages its own refs for focus management.
 */
import { useCallback, useRef } from 'react';
import type { ActiveView } from './useAppState';
import type { AnimationState } from './useAnimation';
import type { MarkerState, PlaceLayer } from '../types/Place';

export interface AppHandlerDeps {
  // State setters from useAppState
  setActiveView: (view: ActiveView) => void;
  setMapReady: (ready: boolean) => void;
  setSelectedPlace: (id: string | null) => void;
  setHighlightedPlaceId: (id: string | null) => void;
  setTargetPlaceId: (id: string | null) => void;
  setNavigationHighlight: (id: string | null) => void;
  setActiveLayer: (layer: 'all' | PlaceLayer) => void;
  setStatusMessage: (msg: string) => void;
  setCollapsedMonths: (fn: (prev: Set<string>) => Set<string>) => void;
  triggerResetView: () => void;

  // State values from useAppState
  selectedPlace: string | null;
  navigationHighlight: string | null;

  // Timeout hooks
  setHighlightTimer: (fn: () => void, delay: number) => void;
  setNavigationTimer: (fn: () => void, delay: number) => void;
  clearNavigationTimer: () => void;

  // From useAnimation
  animationState: AnimationState;
  resetAnimation: () => void;

  // From useMarkerState
  getMarkerState: (placeId: string) => MarkerState;
  handleMarkerPopupClose: () => string | null;
  markPreviousAsViewed: (placeId: string) => void;
  markVisited: (placeId: string) => void;
  clearHighlight: () => void;
  resetMarkerState: () => void;

  // From useTimeMachine
  isTimeMachineMode: boolean;
  enterTimeMachine: () => void;
  exitTimeMachine: () => void;
  pauseTimeMachine: () => void;
  setTimeMachineProgress: (progress: number) => void;
  advanceToNextPlace: () => string | null;
  hasTimelinePlaces: boolean;
}

export interface AppHandlers {
  handleMarkerClick: (id: string) => void;
  handlePopupClose: () => void;
  handleMapReady: () => void;
  handleReplayAnimation: () => void;
  handleEnterTimeMachine: () => void;
  handleExitTimeMachine: () => void;
  handleTimeMachineProgressChange: (progress: number) => void;
  handleLogoClick: () => void;
  handleViewToggle: (view: ActiveView) => void;
  handleShowInJournal: () => void;
  handleShowOnMap: (placeId: string) => void;
  handleMapInteraction: () => void;
  handleCenterComplete: () => void;
  handleToggleMonthCollapse: (monthKey: string) => void;
  getEnhancedMarkerState: (placeId: string) => MarkerState;
  // Refs for focus management - attach to DOM elements
  mapPanelRef: React.RefObject<HTMLDivElement | null>;
  journalPanelRef: React.RefObject<HTMLDivElement | null>;
}

export function useAppHandlers(deps: AppHandlerDeps): AppHandlers {
  const {
    setActiveView,
    setMapReady,
    setSelectedPlace,
    setHighlightedPlaceId,
    setTargetPlaceId,
    setNavigationHighlight,
    setActiveLayer,
    setStatusMessage,
    setCollapsedMonths,
    triggerResetView,
    selectedPlace,
    navigationHighlight,
    setHighlightTimer,
    setNavigationTimer,
    clearNavigationTimer,
    animationState,
    resetAnimation,
    getMarkerState,
    handleMarkerPopupClose,
    markPreviousAsViewed,
    markVisited,
    clearHighlight,
    resetMarkerState,
    isTimeMachineMode,
    enterTimeMachine,
    exitTimeMachine,
    pauseTimeMachine,
    setTimeMachineProgress,
    advanceToNextPlace,
    hasTimelinePlaces,
  } = deps;

  // Refs for focus management (H1, H5)
  const mapPanelRef = useRef<HTMLDivElement | null>(null);
  const journalPanelRef = useRef<HTMLDivElement | null>(null);
  // Ref to track the element that triggered the popup for focus return
  const popupTriggerRef = useRef<HTMLElement | null>(null);

  const handleMarkerClick = useCallback((id: string) => {
    // Only allow marker clicks after animation is complete (Story 2.1 dependency)
    if (animationState !== 'complete') return;
    // Store the trigger element for focus return when popup closes (H1 accessibility)
    popupTriggerRef.current = document.activeElement as HTMLElement | null;
    clearHighlight(); // Clear highlight when selecting a marker
    setSelectedPlace(id);
  }, [animationState, clearHighlight, setSelectedPlace]);

  const handlePopupClose = useCallback(() => {
    const closingPlace = selectedPlace;
    setSelectedPlace(null);

    let nextPlaceId: string | null = null;

    // In Time Machine mode, advance to next place on the timeline
    if (isTimeMachineMode) {
      // Mark the closed place as visited so it turns gray
      if (closingPlace) {
        markVisited(closingPlace);
      }
      nextPlaceId = advanceToNextPlace();
      // Highlight the next marker so it glows
      if (nextPlaceId) {
        setNavigationHighlight(nextPlaceId);
        // Clear highlight after 3 seconds
        setNavigationTimer(() => {
          setNavigationHighlight(null);
        }, 3000);
      }
    } else {
      // Normal mode: mark visited, highlight next
      nextPlaceId = handleMarkerPopupClose();
    }

    // Center map on next place if there is one
    if (nextPlaceId) {
      setTargetPlaceId(nextPlaceId);
    }

    // Return focus to the element that triggered the popup (H1 accessibility)
    requestAnimationFrame(() => {
      if (popupTriggerRef.current && document.body.contains(popupTriggerRef.current)) {
        popupTriggerRef.current.focus();
      } else {
        // Fallback: focus the map panel if trigger is no longer available
        mapPanelRef.current?.focus();
      }
      popupTriggerRef.current = null;
    });
  }, [handleMarkerPopupClose, isTimeMachineMode, advanceToNextPlace, selectedPlace, markVisited, setNavigationTimer, setNavigationHighlight, setSelectedPlace, setTargetPlaceId]);

  const handleMapReady = useCallback(() => {
    setMapReady(true);
  }, [setMapReady]);

  const handleReplayAnimation = useCallback(() => {
    // Reset all state for fresh animation
    resetAnimation();
    resetMarkerState();
    setSelectedPlace(null);
    // Switch to map view if in journal
    setActiveView('map');
    // Animation will auto-start due to useEffect detecting idle state
  }, [resetAnimation, resetMarkerState, setSelectedPlace, setActiveView]);

  // Epic 9: Handle Time Machine enter
  const handleEnterTimeMachine = useCallback(() => {
    if (!hasTimelinePlaces || animationState !== 'complete') return;
    setSelectedPlace(null); // Close any open popup
    setActiveView('map'); // Switch to map view
    resetMarkerState(); // Reset visited/highlighted markers for fresh Time Machine experience
    enterTimeMachine();
  }, [hasTimelinePlaces, animationState, enterTimeMachine, resetMarkerState, setSelectedPlace, setActiveView]);

  // Epic 9: Handle Time Machine exit
  const handleExitTimeMachine = useCallback(() => {
    exitTimeMachine();
  }, [exitTimeMachine]);

  // Epic 9: Handle Time Machine progress change (pause playback during scrubbing)
  const handleTimeMachineProgressChange = useCallback((progress: number) => {
    pauseTimeMachine();
    setTimeMachineProgress(progress);
  }, [pauseTimeMachine, setTimeMachineProgress]);

  // Story 8-5: Reset to default view when logo is clicked
  const handleLogoClick = useCallback(() => {
    setActiveLayer('all');
    setSelectedPlace(null);
    setActiveView('map');
    resetMarkerState(); // Reset visited/highlighted state for fresh experience
    // Force map to recenter even if already on 'all' layer
    triggerResetView();
  }, [resetMarkerState, setActiveLayer, setSelectedPlace, setActiveView, triggerResetView]);

  const handleViewToggle = useCallback((view: ActiveView) => {
    setActiveView(view);
    // H1: Move focus to new panel after view switch
    requestAnimationFrame(() => {
      if (view === 'map') {
        mapPanelRef.current?.focus();
      } else {
        journalPanelRef.current?.focus();
      }
    });
  }, [setActiveView]);

  const handleShowInJournal = useCallback(() => {
    // H3: Guard against double-tap race condition
    if (!selectedPlace) return;

    const placeId = selectedPlace;

    // 1. Close popup
    setSelectedPlace(null);

    // 2. Switch to journal view
    setActiveView('journal');

    // 3. Set highlighted place (for scroll + highlight)
    setHighlightedPlaceId(placeId);

    // 4. Set navigation highlight so marker stays glowing when returning to map via tab
    setNavigationHighlight(placeId);

    // H1: Move focus to journal panel after view switch
    requestAnimationFrame(() => {
      journalPanelRef.current?.focus();
    });

    // 5. Auto-clear highlight after 3 seconds
    setHighlightTimer(() => {
      setHighlightedPlaceId(null);
      setNavigationHighlight(null);
      // H6: Announce highlight expiry to screen readers
      setStatusMessage('Entry highlight cleared');
      setTimeout(() => setStatusMessage(''), 1000);
    }, 3000);
  }, [selectedPlace, setHighlightTimer, setSelectedPlace, setActiveView, setHighlightedPlaceId, setNavigationHighlight, setStatusMessage]);

  const handleShowOnMap = useCallback((placeId: string) => {
    // H3: Guard against invalid calls
    if (!placeId) return;

    // Mark all chronologically earlier places as viewed (AC: #3 - batch update)
    markPreviousAsViewed(placeId);

    // 1. Switch to map view
    setActiveView('map');

    // 2. Set target place for centering
    setTargetPlaceId(placeId);

    // 3. Highlight the marker
    setNavigationHighlight(placeId);

    // H1: Move focus to map panel after view switch
    requestAnimationFrame(() => {
      mapPanelRef.current?.focus();
    });

    // 4. Clear highlight after 3 seconds
    setNavigationTimer(() => {
      setNavigationHighlight(null);
      // H6: Announce highlight expiry to screen readers
      setStatusMessage('Marker highlight cleared');
      setTimeout(() => setStatusMessage(''), 1000);
    }, 3000);
  }, [markPreviousAsViewed, setNavigationTimer, setActiveView, setTargetPlaceId, setNavigationHighlight, setStatusMessage]);

  // M5: Clear navigation highlight when user interacts with map
  const handleMapInteraction = useCallback(() => {
    if (navigationHighlight) {
      setNavigationHighlight(null);
      clearNavigationTimer();
    }
  }, [navigationHighlight, clearNavigationTimer, setNavigationHighlight]);

  const handleCenterComplete = useCallback(() => {
    // Clear target place after centering completes
    setTargetPlaceId(null);
  }, [setTargetPlaceId]);

  // Story 7.3: Toggle month collapse state
  const handleToggleMonthCollapse = useCallback((monthKey: string) => {
    setCollapsedMonths((prev) => {
      const next = new Set(prev);
      if (next.has(monthKey)) {
        next.delete(monthKey);
      } else {
        next.add(monthKey);
      }
      return next;
    });
  }, [setCollapsedMonths]);

  // Enhanced getMarkerState to include navigation highlight
  const getEnhancedMarkerState = useCallback(
    (placeId: string) => {
      if (navigationHighlight === placeId) {
        return 'highlighted' as MarkerState;
      }
      return getMarkerState(placeId);
    },
    [getMarkerState, navigationHighlight]
  );

  return {
    handleMarkerClick,
    handlePopupClose,
    handleMapReady,
    handleReplayAnimation,
    handleEnterTimeMachine,
    handleExitTimeMachine,
    handleTimeMachineProgressChange,
    handleLogoClick,
    handleViewToggle,
    handleShowInJournal,
    handleShowOnMap,
    handleMapInteraction,
    handleCenterComplete,
    handleToggleMonthCollapse,
    getEnhancedMarkerState,
    mapPanelRef,
    journalPanelRef,
  };
}
